import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  AdminCreateShiftSchema,
  AdminEditShiftSchema,
  CurrentShiftSchema,
  NurseClockInSchema,
  NurseClockOutSchema,
  NurseRatesSchema,
  NurseShiftSchema,
  PayrollPaystubSchema,
  PayrollPeriodTotalSchema,
  UpdateNurseRatesSchema,
  type AdminCreateShiftInput,
  type AdminEditShiftInput,
  type CurrentShift,
  type NurseClockInInput,
  type NurseClockOutInput,
  type NurseRates,
  type NurseShift,
  type PayrollPaystub,
  type PayrollPeriodTotal,
  type UpdateNurseRatesInput,
} from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

const RatesListSchema = z.object({ items: z.array(NurseRatesSchema) });
const ShiftsListSchema = z.object({ items: z.array(NurseShiftSchema) });

export const payrollKeys = {
  all: ['payroll'] as const,
  rates: () => ['payroll', 'rates'] as const,
  currentShift: (nurseId: string | undefined) =>
    ['payroll', 'current-shift', nurseId] as const,
  shifts: (nurseId: string | undefined, desde: string | undefined, hasta: string | undefined) =>
    ['payroll', 'shifts', { nurseId, desde, hasta }] as const,
  paystub: (nurseId: string, year: number, month: number, quincena: 1 | 2) =>
    ['payroll', 'paystub', { nurseId, year, month, quincena }] as const,
  paystubAll: (year: number, month: number, quincena: 1 | 2) =>
    ['payroll', 'paystub-all', { year, month, quincena }] as const,
};

/* -------------------------------------------------------------------------- */
/* Tarifas                                                                    */
/* -------------------------------------------------------------------------- */

export function useNurseRates(): UseQueryResult<{ items: NurseRates[] }, Error> {
  return useQuery({
    queryKey: payrollKeys.rates(),
    queryFn: () => apiFetch('/api/payroll/rates', { parseAs: RatesListSchema }),
    staleTime: 60_000,
  });
}

export function useUpdateNurseRates(): UseMutationResult<
  NurseRates,
  Error,
  { nurseId: string; body: UpdateNurseRatesInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ nurseId, body }) => {
      const v = UpdateNurseRatesSchema.parse(body);
      return apiFetch<NurseRates>(`/api/payroll/rates/${encodeURIComponent(nurseId)}`, {
        method: 'PATCH',
        body: v,
        parseAs: NurseRatesSchema,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: payrollKeys.rates() });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Mi turno + clock-in/out                                                    */
/* -------------------------------------------------------------------------- */

export function useCurrentShift(
  nurseId: string | undefined,
): UseQueryResult<CurrentShift, Error> {
  return useQuery({
    queryKey: payrollKeys.currentShift(nurseId),
    queryFn: () => {
      if (nurseId === undefined) throw new Error('nurseId requerido');
      const qs = new URLSearchParams({ nurseId });
      return apiFetch<CurrentShift>(`/api/payroll/current-shift?${qs}`, {
        parseAs: CurrentShiftSchema,
      });
    },
    enabled: nurseId !== undefined,
    // Refresca cada 30s mientras la pestaña está activa para que el contador
    // de tiempo y el "procedimientos hoy" estén actualizados sin recargar.
    refetchInterval: 30_000,
  });
}

export function useClockIn(): UseMutationResult<NurseShift, Error, NurseClockInInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<NurseShift>('/api/payroll/clock-in', {
        method: 'POST',
        body: NurseClockInSchema.parse(body),
        parseAs: NurseShiftSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payroll', 'current-shift'] });
      void qc.invalidateQueries({ queryKey: ['payroll', 'shifts'] });
    },
  });
}

export function useClockOut(): UseMutationResult<NurseShift, Error, NurseClockOutInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<NurseShift>('/api/payroll/clock-out', {
        method: 'POST',
        body: NurseClockOutSchema.parse(body),
        parseAs: NurseShiftSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payroll', 'current-shift'] });
      void qc.invalidateQueries({ queryKey: ['payroll', 'shifts'] });
      void qc.invalidateQueries({ queryKey: ['payroll', 'paystub'] });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Histórico de jornadas                                                      */
/* -------------------------------------------------------------------------- */

export function useNurseShifts(args: {
  nurseId?: string;
  desde?: string;
  hasta?: string;
}): UseQueryResult<{ items: NurseShift[] }, Error> {
  return useQuery({
    queryKey: payrollKeys.shifts(args.nurseId, args.desde, args.hasta),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (args.nurseId !== undefined) qs.set('nurseId', args.nurseId);
      if (args.desde !== undefined) qs.set('desde', args.desde);
      if (args.hasta !== undefined) qs.set('hasta', args.hasta);
      const url = qs.toString() === '' ? '/api/payroll/shifts' : `/api/payroll/shifts?${qs}`;
      return apiFetch(url, { parseAs: ShiftsListSchema });
    },
    staleTime: 15_000,
  });
}

/**
 * Registra manualmente una jornada (admin) — día que la enfermera olvidó
 * marcar, incluso anterior. Invalida todo payroll (cambia la liquidación).
 */
export function useCreateShift(): UseMutationResult<NurseShift, Error, AdminCreateShiftInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<NurseShift>('/api/payroll/shifts', {
        method: 'POST',
        body: AdminCreateShiftSchema.parse(body),
        parseAs: NurseShiftSchema,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: payrollKeys.all }),
  });
}

/**
 * Ajusta una jornada existente (admin): cerrar, reabrir o corregir horas.
 * Invalida todo payroll (los totales de liquidación cambian).
 */
export function useEditShift(): UseMutationResult<
  NurseShift,
  Error,
  { id: string; body: AdminEditShiftInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) =>
      apiFetch<NurseShift>(`/api/payroll/shifts/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: AdminEditShiftSchema.parse(body),
        parseAs: NurseShiftSchema,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: payrollKeys.all }),
  });
}

/**
 * Elimina una jornada (turno) por id — corrección administrativa de una entrada
 * marcada por error. Invalida todo payroll (los totales de liquidación cambian).
 */
export function useDeleteShift(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/payroll/shifts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: payrollKeys.all }),
  });
}

/* -------------------------------------------------------------------------- */
/* Paystub quincenal                                                          */
/* -------------------------------------------------------------------------- */

export function usePaystubAll(args: {
  year: number;
  month: number;
  quincena: 1 | 2;
  enabled?: boolean;
}): UseQueryResult<PayrollPeriodTotal, Error> {
  return useQuery({
    queryKey: payrollKeys.paystubAll(args.year, args.month, args.quincena),
    queryFn: () => {
      const qs = new URLSearchParams({
        year: String(args.year),
        month: String(args.month),
        quincena: String(args.quincena),
      });
      return apiFetch<PayrollPeriodTotal>(`/api/payroll/paystub-all?${qs}`, {
        parseAs: PayrollPeriodTotalSchema,
      });
    },
    enabled: args.enabled ?? true,
    staleTime: 30_000,
  });
}

export function usePaystub(args: {
  nurseId: string | undefined;
  year: number;
  month: number;
  quincena: 1 | 2;
  enabled?: boolean;
}): UseQueryResult<PayrollPaystub, Error> {
  return useQuery({
    queryKey:
      args.nurseId !== undefined
        ? payrollKeys.paystub(args.nurseId, args.year, args.month, args.quincena)
        : (['payroll', 'paystub', '__missing__'] as const),
    queryFn: () => {
      if (args.nurseId === undefined) throw new Error('nurseId requerido');
      const qs = new URLSearchParams({
        nurseId: args.nurseId,
        year: String(args.year),
        month: String(args.month),
        quincena: String(args.quincena),
      });
      return apiFetch<PayrollPaystub>(`/api/payroll/paystub?${qs}`, {
        parseAs: PayrollPaystubSchema,
      });
    },
    enabled: args.nurseId !== undefined && (args.enabled ?? true),
    staleTime: 30_000,
  });
}
